import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';

/**
 * InsuranceChangeRequests — staff approval surface for Scenario 3:
 * patients who entered an insurance different from what's on Athena.
 *
 * Approving runs the destructive sequence (delete old + create new +
 * attach card) on the backend with a re-create rollback if the create
 * fails. Rejecting just records the staff decision — they'll call the
 * patient outside this UI.
 */
function InsuranceChangeRequests({ practiceId }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [requests, setRequests] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [approvalConfirm, setApprovalConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!practiceId) return;
    try {
      setLoading(true);
      const res = await api.getInsuranceChangeRequests(practiceId);
      if (res.success) {
        setRequests(res.data || []);
        setPendingCount(res.pending_count || 0);
      }
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [practiceId]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [load]);

  // Deep-link from Care Hub Calendar: ?focus=<icr_id> auto-opens that ICR's
  // review modal so staff lands on the right thing. Strips the param from the
  // URL after consuming it so a refresh doesn't re-trigger the open after
  // staff has closed the modal.
  useEffect(() => {
    const focusId = parseInt(searchParams.get('focus'), 10);
    if (!focusId || loading || requests.length === 0 || selected) return;
    const target = requests.find(r => r.id === focusId);
    if (target) {
      openRequest(target);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requests, loading]);

  const openRequest = async (req) => {
    try {
      const res = await api.getInsuranceChangeRequest(practiceId, req.id);
      if (res.success) setSelected(res.data);
    } catch (err) {
      setError(err.message);
    }
  };

  const closeModal = () => {
    setSelected(null);
    setRejectReason('');
    setApprovalConfirm('');
  };

  const handleApprove = async () => {
    if (approvalConfirm !== 'APPROVE') return;
    try {
      setBusy(true);
      await api.approveInsuranceChangeRequest(practiceId, selected.id);
      closeModal();
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async () => {
    try {
      setBusy(true);
      await api.rejectInsuranceChangeRequest(practiceId, selected.id, rejectReason || 'Staff rejected — will contact patient');
      closeModal();
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!practiceId) {
    return <div style={s.page}><p style={{ color: '#666' }}>Select a practice to view insurance change requests.</p></div>;
  }

  return (
    <div style={s.page}>
      <div style={s.header}>
        <h2 style={s.title}>Insurance Change Requests {pendingCount > 0 ? `(${pendingCount} pending)` : ''}</h2>
        <button onClick={load} style={s.refreshBtn} disabled={loading}>{loading ? 'Loading...' : 'Refresh'}</button>
      </div>
      <p style={s.subtitle}>
        Patients who submitted a different insurance than what's on Athena. Approving runs the deactivate-old / create-new / attach-card sequence on Athena. Verify with the patient before approving — insurance changes hit billing and claims.
      </p>

      {error && <div style={s.error}>Error: {error}</div>}

      {requests.length === 0 && !loading && (
        <div style={s.empty}>No pending insurance change requests.</div>
      )}

      <div style={s.list}>
        {requests.map(req => {
          const existing = typeof req.existing_snapshot === 'string' ? JSON.parse(req.existing_snapshot) : req.existing_snapshot;
          const proposed = typeof req.proposed_snapshot === 'string' ? JSON.parse(req.proposed_snapshot) : req.proposed_snapshot;
          return (
            <div key={req.id} style={s.card}>
              <div style={s.cardHeader}>
                <div>
                  <div style={s.patientName}>
                    {req.patient_name || `Patient #${req.patient_id}`}
                    {req.status && req.status !== 'pending_approval' && (
                      <span style={s.failedBadge}>
                        {req.status === 'failed_at_delete' && 'Delete failed'}
                        {req.status === 'failed_at_create' && 'Create failed — old policy deleted'}
                        {req.status === 'failed_at_create_restored' && 'Create failed — old policy restored'}
                        {req.status === 'failed_at_rollback' && 'Create + rollback failed — patient has no insurance'}
                      </span>
                    )}
                  </div>
                  <div style={s.meta}>
                    Sequence {req.sequence_number} · {new Date(req.created_at).toLocaleString()}
                    {req.appointment_date && ` · Appt ${new Date(req.appointment_date).toLocaleDateString()}`}
                  </div>
                  {req.failure_reason && (
                    <div style={s.failureReason}>
                      <strong>Reason:</strong> {req.failure_reason}
                    </div>
                  )}
                </div>
                <button onClick={() => openRequest(req)} style={s.reviewBtn}>
                  {req.status === 'pending_approval' ? 'Review →' : 'Details'}
                </button>
              </div>
              <div style={s.diffRow}>
                <div style={{ ...s.diffBox, background: '#fef2f2', borderColor: '#fecaca' }}>
                  <div style={s.diffLabel}>Current on Athena</div>
                  <div style={s.planName}>{existing?.PlanName || '—'}</div>
                  <div style={s.meta}>Member: {existing?.MemberID || '—'} · Group: {existing?.GroupNumber || '—'}</div>
                </div>
                <div style={s.diffArrow}>→</div>
                <div style={{ ...s.diffBox, background: '#f0fdf4', borderColor: '#bbf7d0' }}>
                  <div style={s.diffLabel}>Patient submitted</div>
                  <div style={s.planName}>{proposed?.PlanName || '—'}</div>
                  <div style={s.meta}>Member: {proposed?.MemberID || '—'} · Group: {proposed?.GroupNumber || '—'}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {selected && (
        <ApprovalModal
          request={selected}
          onClose={closeModal}
          onApprove={handleApprove}
          onReject={handleReject}
          rejectReason={rejectReason}
          setRejectReason={setRejectReason}
          approvalConfirm={approvalConfirm}
          setApprovalConfirm={setApprovalConfirm}
          busy={busy}
        />
      )}
    </div>
  );
}

function ApprovalModal({ request, onClose, onApprove, onReject, rejectReason, setRejectReason, approvalConfirm, setApprovalConfirm, busy }) {
  const existing = request.existing_snapshot;
  const proposed = request.proposed_snapshot;
  const cardKeys = request.new_card_image_s3_keys || [];

  return (
    <div style={s.modalBackdrop} onClick={onClose}>
      <div style={s.modal} onClick={(e) => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <div style={s.modalTitle}>Insurance Change · Sequence {request.sequence_number}</div>
          <button style={s.closeBtn} onClick={onClose}>×</button>
        </div>

        <div style={s.diffGrid}>
          <div style={{ ...s.diffPanel, background: '#fef2f2', borderColor: '#fecaca' }}>
            <div style={s.diffPanelTitle}>Currently on Athena</div>
            <FieldRow label="Plan" value={existing?.PlanName} />
            <FieldRow label="Member ID" value={existing?.MemberID} />
            <FieldRow label="Group #" value={existing?.GroupNumber} />
            <FieldRow label="Policyholder" value={existing?.PolicyHolderName} />
            <FieldRow label="Relationship" value={existing?.PolicyHolderRelationship} />
            <FieldRow label="Effective" value={existing?.EffectiveDate} />
            <FieldRow label="Athena ID" value={existing?.InsuranceID} />
          </div>
          <div style={{ ...s.diffPanel, background: '#f0fdf4', borderColor: '#bbf7d0' }}>
            <div style={s.diffPanelTitle}>Patient submitted</div>
            <FieldRow label="Plan" value={proposed?.PlanName} />
            <FieldRow label="Member ID" value={proposed?.MemberID} />
            <FieldRow label="Group #" value={proposed?.GroupNumber} />
            <FieldRow label="Policyholder" value={proposed?.PolicyHolderName} />
            <FieldRow label="Relationship" value={proposed?.PolicyHolderRelationship} />
            <FieldRow label="New card uploaded" value={proposed?.NewCardUploaded ? 'Yes' : 'No'} />
            {cardKeys.length > 0 && <FieldRow label="Card images" value={`${cardKeys.length} on file`} />}
          </div>
        </div>

        <div style={s.warning}>
          ⚠ Approving will <strong>deactivate</strong> the current insurance on Athena and create the new one.
          This affects billing and claims. Verify with the patient before approving.
          If the create fails after the delete succeeds, the old policy is re-created automatically.
        </div>

        <div style={s.modalActions}>
          <div style={s.approveSection}>
            <label style={s.confirmLabel}>
              <strong>Confirmation:</strong> type the word <code>APPROVE</code> exactly to enable the button.
              <span style={s.confirmHint}>This is not a comments field — leave any patient notes in the appointment record instead.</span>
              <input
                type="text"
                value={approvalConfirm}
                onChange={(e) => setApprovalConfirm(e.target.value)}
                style={s.confirmInput}
                disabled={busy}
                placeholder="Type APPROVE here"
                aria-label="Type the word APPROVE to enable the approve and replace button"
              />
            </label>
            <button
              onClick={onApprove}
              disabled={approvalConfirm !== 'APPROVE' || busy}
              style={{ ...s.approveBtn, opacity: approvalConfirm === 'APPROVE' && !busy ? 1 : 0.5 }}
            >
              {busy ? 'Executing...' : 'Approve & Replace'}
            </button>
          </div>
          <div style={s.rejectSection}>
            <label style={s.confirmLabel}>
              Reject reason (optional)
              <input
                type="text"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                style={s.confirmInput}
                disabled={busy}
                placeholder="Will call patient"
              />
            </label>
            <button onClick={onReject} disabled={busy} style={s.rejectBtn}>Reject</button>
          </div>
          <button onClick={onClose} disabled={busy} style={s.cancelBtn}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function FieldRow({ label, value }) {
  return (
    <div style={s.fieldRow}>
      <span style={s.fieldLabel}>{label}</span>
      <span style={s.fieldValue}>{value || '—'}</span>
    </div>
  );
}

const s = {
  page: { padding: '24px 32px', maxWidth: 1200, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  title: { margin: 0, fontSize: 24, fontWeight: 700, color: '#0f172a' },
  subtitle: { fontSize: 13, color: '#64748b', marginBottom: 20 },
  refreshBtn: { padding: '8px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  error: { padding: 12, background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 8, marginBottom: 16 },
  empty: { padding: 32, textAlign: 'center', color: '#9ca3af', background: '#f8fafc', borderRadius: 8 },
  list: { display: 'flex', flexDirection: 'column', gap: 12 },
  card: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 },
  patientName: { fontSize: 16, fontWeight: 600, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  failedBadge: { display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700, background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca', textTransform: 'uppercase', letterSpacing: 0.4 },
  failureReason: { marginTop: 6, padding: '6px 10px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, fontSize: 12, color: '#7f1d1d', maxWidth: 600, wordBreak: 'break-word' },
  meta: { fontSize: 12, color: '#64748b', marginTop: 2 },
  reviewBtn: { padding: '6px 14px', background: '#eff6ff', color: '#1e40af', border: '1px solid #3b82f6', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  diffRow: { display: 'flex', alignItems: 'center', gap: 8 },
  diffBox: { flex: 1, padding: 10, border: '1px solid', borderRadius: 8 },
  diffLabel: { fontSize: 10, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 },
  planName: { fontSize: 14, fontWeight: 600, color: '#0f172a' },
  diffArrow: { fontSize: 20, color: '#9ca3af' },
  modalBackdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 },
  modal: { background: '#fff', borderRadius: 12, padding: 24, width: '90%', maxWidth: 720, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  modalTitle: { fontSize: 18, fontWeight: 700, color: '#0f172a' },
  closeBtn: { background: 'transparent', border: 'none', fontSize: 24, color: '#9ca3af', cursor: 'pointer', lineHeight: 1 },
  diffGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 },
  diffPanel: { padding: 12, border: '1px solid', borderRadius: 8 },
  diffPanelTitle: { fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', marginBottom: 8 },
  fieldRow: { display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid rgba(0,0,0,0.05)', fontSize: 13 },
  fieldLabel: { color: '#64748b' },
  fieldValue: { color: '#0f172a', fontWeight: 500, maxWidth: '60%', textAlign: 'right', wordBreak: 'break-word' },
  warning: { padding: 12, background: '#fef3c7', border: '1px solid #fcd34d', color: '#92400e', borderRadius: 8, fontSize: 13, marginBottom: 16 },
  modalActions: { display: 'flex', flexDirection: 'column', gap: 12 },
  approveSection: { display: 'flex', gap: 8, alignItems: 'flex-end' },
  rejectSection: { display: 'flex', gap: 8, alignItems: 'flex-end' },
  confirmLabel: { flex: 1, display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#64748b' },
  confirmHint: { fontStyle: 'italic', color: '#94a3b8', fontSize: 11, marginTop: -2 },
  confirmInput: { padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 13 },
  approveBtn: { padding: '10px 20px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  rejectBtn: { padding: '10px 20px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  cancelBtn: { padding: '8px 16px', background: 'transparent', color: '#64748b', border: '1px solid #e5e7eb', borderRadius: 6, cursor: 'pointer', fontSize: 13, alignSelf: 'flex-end' },
};

export default InsuranceChangeRequests;
